"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import Reveal from "../Common/Reveal";
import {
  ArrowRight,
  Instagram,
  Linkedin,
  Mail,
  Phone,
} from "../Common/Icons";

type Status = "idle" | "sending" | "success" | "error";

const directLinks = [
  {
    Icon: Mail,
    label: "contact@devestsolutions.com",
    href: "mailto:contact@devestsolutions.com",
  },
  { Icon: Phone, label: "+34 694 230 150", href: "tel:+34694230150" },
  {
    Icon: Linkedin,
    label: "/devest-solutions",
    href: "https://www.linkedin.com/company/devest-solutions",
  },
  {
    Icon: Instagram,
    label: "@devestsolutions",
    href: "https://www.instagram.com/devestsolutions",
  },
];

const Contact = () => {
  const t = useTranslations("component.contact");
  const [status, setStatus] = useState<Status>("idle");

  const sendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    setStatus("sending");

    try {
      const response = await fetch("/api/message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contactName: data.get("contactName"),
          email: data.get("email"),
          companyName: data.get("companyName"),
          interest: data.get("interest"),
          message: data.get("message"),
        }),
      });

      const result = (await response.json()) as { sent: boolean };

      if (result.sent) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const sending = status === "sending";

  return (
    <section id="contact" className="section relative overflow-hidden">
      <div aria-hidden="true" className="glow-corner absolute inset-0 -z-10" />
      <div className="container">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <span className="eyebrow mb-4">{t("eyebrow")}</span>
            <h2 className="heading-lg text-balance">{t("title")}</h2>
            <p className="lead mt-4">{t("description")}</p>

            <div className="mt-10">
              <p className="mb-4 text-sm font-semibold text-black dark:text-white">
                {t("directTitle")}
              </p>
              <ul className="space-y-3">
                {directLinks.map(({ Icon, label, href }) => (
                  <li key={href}>
                    <a
                      href={href}
                      target={href.startsWith("http") ? "_blank" : undefined}
                      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="group inline-flex items-center gap-3 text-sm text-body-color transition hover:text-primary dark:text-body-color-dark dark:hover:text-primary-300"
                    >
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-stroke bg-white transition group-hover:border-primary/40 dark:border-stroke-dark dark:bg-white/5">
                        <Icon size={16} />
                      </span>
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-body-color/80 dark:text-body-color-dark/80">
                {t("responseTime")}
              </p>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-7">
            <form
              onSubmit={sendMessage}
              className="card p-6 sm:p-8 md:p-10"
              noValidate={false}
            >
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="contactName" className="label">
                    {t("fields.name.label")}
                  </label>
                  <input
                    required
                    id="contactName"
                    name="contactName"
                    type="text"
                    autoComplete="name"
                    placeholder={t("fields.name.placeholder")}
                    className="field"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="label">
                    {t("fields.email.label")}
                  </label>
                  <input
                    required
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t("fields.email.placeholder")}
                    className="field"
                  />
                </div>
                <div>
                  <label htmlFor="companyName" className="label">
                    {t("fields.company.label")}
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    autoComplete="organization"
                    placeholder={t("fields.company.placeholder")}
                    className="field"
                  />
                </div>
                <div>
                  <label htmlFor="interest" className="label">
                    {t("fields.interest.label")}
                  </label>
                  <div className="relative">
                    <select
                      id="interest"
                      name="interest"
                      defaultValue="placeholder"
                      className="field appearance-none pr-10"
                    >
                      <option value="placeholder" disabled>
                        {t("fields.interest.placeholder")}
                      </option>
                      <option value="teamExtension">
                        {t("fields.interest.items.teamExtension")}
                      </option>
                      <option value="consultancy">
                        {t("fields.interest.items.consultancy")}
                      </option>
                      <option value="mvp">{t("fields.interest.items.mvp")}</option>
                      <option value="other">
                        {t("fields.interest.items.other")}
                      </option>
                    </select>
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-body-color dark:text-body-color-dark"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="message" className="label">
                    {t("fields.message.label")}
                  </label>
                  <textarea
                    required
                    id="message"
                    name="message"
                    rows={5}
                    placeholder={t("fields.message.placeholder")}
                    className="field resize-none"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary self-start"
                >
                  {sending ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      {t("sendingButton")}
                    </>
                  ) : (
                    <>
                      {t("sendButton")}
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>

                <p
                  role="status"
                  aria-live="polite"
                  className={`text-sm ${
                    status === "success"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : status === "error"
                        ? "text-red-600 dark:text-red-400"
                        : "sr-only"
                  }`}
                >
                  {status === "success" && t("message.success")}
                  {status === "error" && t("message.error")}
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
